import { useEffect, useState } from 'react'
import { SEARCH_PAGE_CITY_MAP, SearchPageCity } from 'src/constants/searchPages'
import useUserLocation from 'src/hooks/useUserLocation'

export type CitySource = 'url' | 'browser-geo' | 'ip' | 'fallback'

export type EffectiveCity = {
  city: SearchPageCity
  cityName: string
  source: CitySource
  /** True once any signal (URL, geo, IP, or fallback) has resolved on the client. */
  isReady: boolean
}

const VALID_CITIES = new Set<string>(Object.values(SearchPageCity))

export function parseInitialCity(raw: unknown): SearchPageCity | null {
  if (typeof raw !== 'string') return null
  return VALID_CITIES.has(raw) ? (raw as SearchPageCity) : null
}

function readCityFromUrl(): SearchPageCity | null {
  if (typeof window === 'undefined') return null
  return parseInitialCity(
    new URLSearchParams(window.location.search).get('city')
  )
}

/**
 * Marketing-aware city resolver.
 *
 * Priority order:
 *  1. SSR-resolved `initialCity` prop (passed by the page from
 *     `getServerSideProps` after validating `?city=<slug>`) — no flash
 *  2. Client-side `?city=<slug>` URL param read post-mount (defensive
 *     fallback for cases where the page is statically rendered)
 *  3. Browser geolocation (when consented)
 *  4. IP-based geolocation (always available for MX IPs)
 *  5. Fallback: Monterrey (matches `useUserLocation` default)
 *
 * When `initialCity` is provided, the hook returns that value on the very
 * first render — both on SSR and on client hydration — so there is no
 * post-mount swap and no visible flash. The client-side URL read only
 * runs as a fallback when `initialCity` is not provided.
 *
 * Use this hook on marketing surfaces where the URL param should override
 * actual user location. Do NOT use it on map/listing surfaces where
 * `useUserLocation` is the source of truth.
 */
const useEffectiveCity = (
  initialCity?: SearchPageCity | null
): EffectiveCity => {
  // When SSR has already resolved the city, seed state with it directly.
  // SSR and first client render both produce the same output → no
  // hydration mismatch, no swap.
  const [urlCity, setUrlCity] = useState<SearchPageCity | null>(
    initialCity ?? null
  )
  const [mounted, setMounted] = useState<boolean>(!!initialCity)

  useEffect(() => {
    if (initialCity) {
      // SSR already resolved — nothing to do client-side.
      return
    }
    setUrlCity(readCityFromUrl())
    setMounted(true)
  }, [initialCity])

  const { closestCity, browserLocation, ipCenter, loading } = useUserLocation()

  // Hydration safety: on the very first client render (before the mount
  // effect runs), ipCenter / browserLocation may already be populated
  // from the Apollo cache / sessionStorage while SSR rendered the
  // Monterrey fallback. Returning the cached value here produces a
  // different cityName / href than SSR → React #418 on /flotillas Hero
  // CTA (`HERO.EXPLORE_INVENTORY_CITY` interpolation drifts to "...en
  // Aguascalientes" while SSR HTML still says "Explorar inventario").
  // Gate every resolved branch on `mounted` so SSR and first hydration
  // render always agree, then swap on the post-mount re-render.
  // `initialCity` seeds `mounted=true` synchronously, so SSR-resolved
  // pages (where SSR already produced the resolved value) are unaffected.
  if (mounted && urlCity) {
    return {
      city: urlCity,
      cityName: SEARCH_PAGE_CITY_MAP[urlCity]?.cityName ?? '',
      source: 'url',
      isReady: true
    }
  }

  if (mounted && browserLocation) {
    return {
      city: closestCity,
      cityName: SEARCH_PAGE_CITY_MAP[closestCity]?.cityName ?? '',
      source: 'browser-geo',
      isReady: true
    }
  }

  if (mounted && ipCenter) {
    return {
      city: closestCity,
      cityName: SEARCH_PAGE_CITY_MAP[closestCity]?.cityName ?? '',
      source: 'ip',
      isReady: true
    }
  }

  return {
    city: SearchPageCity.Monterrey,
    cityName: SEARCH_PAGE_CITY_MAP[SearchPageCity.Monterrey].cityName,
    source: 'fallback',
    isReady: mounted && !loading
  }
}

export default useEffectiveCity
